import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

/**
 * Audits & policies — /api/carehome-manager/audits and /policies.
 *
 * Two related modules behind one service because the UI treats them as one
 * page with two tabs, and because an audit links to a policy version: the
 * audit forms need the policy list, and the policy viewer is opened from an
 * audit. Splitting them would mean every screen importing both anyway.
 *
 * The API is the source of truth for everything derived — overdue, score,
 * review-due, the sign-off blockers — so the pages render what comes back
 * rather than recomputing it. See the controllers for why.
 */

const base = () => `/${getManagerRoutePath()}`;

/** Most write calls answer with the refreshed record; hand it straight back. */
const unwrap = (response) => response.data;

const qualityService = {
  /* ----------------------------------------------------------- audits - */

  // Checklist templates, repeat cycles, areas, staff (owner picker) and the
  // publishable policies an audit can be linked to.
  getAuditMeta: async () => unwrap(await axiosInstance.get(`${base()}/audits/meta`)),

  // The register: every audit with its derived state, plus the header counts.
  getAudits: async () => unwrap(await axiosInstance.get(`${base()}/audits`)),

  getAudit: async (id) => unwrap(await axiosInstance.get(`${base()}/audits/${id}`)),

  // { title, area, owner_id, due_date, repeat_cycle, policy_id, questions: [text] }
  createAudit: async (payload) => unwrap(await axiosInstance.post(`${base()}/audits`, payload)),

  // Assignment details only — never the checklist answers.
  updateAudit: async (id, payload) => unwrap(await axiosInstance.patch(`${base()}/audits/${id}`, payload)),

  // Answer and note are independent: the note autosaves as it is typed, which
  // is why this can be called with only one of them.
  answerCheck: async (auditId, questionId, payload) =>
    unwrap(await axiosInstance.patch(`${base()}/audits/${auditId}/checks/${questionId}`, payload)),

  addCheck: async (auditId, text) =>
    unwrap(await axiosInstance.post(`${base()}/audits/${auditId}/checks`, { text })),

  removeCheck: async (auditId, questionId) =>
    unwrap(await axiosInstance.delete(`${base()}/audits/${auditId}/checks/${questionId}`)),

  saveSummary: async (auditId, summary) =>
    unwrap(await axiosInstance.patch(`${base()}/audits/${auditId}/summary`, { summary })),

  // What sign-off would say right now, including anything still blocking it.
  reviewAudit: async (auditId) => unwrap(await axiosInstance.get(`${base()}/audits/${auditId}/review`)),

  completeAudit: async (auditId, payload) =>
    unwrap(await axiosInstance.post(`${base()}/audits/${auditId}/complete`, payload)),

  addEvidence: async (auditId, files) => {
    const form = new FormData();
    for (const file of files) form.append('evidence', file);
    return unwrap(await axiosInstance.post(`${base()}/audits/${auditId}/evidence`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },

  removeEvidence: async (auditId, fileId) =>
    unwrap(await axiosInstance.delete(`${base()}/audits/${auditId}/evidence/${fileId}`)),

  /* ---------------------------------------------------------- actions - */

  // Every follow-up action, or just one audit's. Completed audits included —
  // that is usually where the outstanding work is.
  getActions: async (auditId) =>
    unwrap(await axiosInstance.get(`${base()}/audits/actions`, {
      params: auditId ? { audit_id: auditId } : {},
    })),

  createAction: async (payload) => unwrap(await axiosInstance.post(`${base()}/audits/actions`, payload)),

  updateAction: async (id, payload) => unwrap(await axiosInstance.patch(`${base()}/audits/actions/${id}`, payload)),

  reopenAction: async (id) => unwrap(await axiosInstance.post(`${base()}/audits/actions/${id}/reopen`)),

  deleteAction: async (id) => unwrap(await axiosInstance.delete(`${base()}/audits/actions/${id}`)),

  /* --------------------------------------------------------- policies - */

  // The library: policies with their current and draft revisions, the category
  // list, the structure templates and the tab counts.
  getPolicies: async () => unwrap(await axiosInstance.get(`${base()}/policies`)),

  // One policy with every revision, history included.
  getPolicy: async (id) => unwrap(await axiosInstance.get(`${base()}/policies/${id}`)),

  // A Word policy converted to HTML so it can be read in the page. Only
  // .docx converts; an older .doc answers 415 and the reader falls back to
  // offering the download.
  previewRevision: async (policyId, revisionId) =>
    unwrap(await axiosInstance.get(`${base()}/policies/${policyId}/revisions/${revisionId}/preview`)),

  // Both draft saves go through multipart, because an uploaded policy sends
  // its file the same way a written one sends its text.
  createPolicy: async (payload) =>
    unwrap(await axiosInstance.post(`${base()}/policies`, toPolicyForm(payload), {
      headers: { 'Content-Type': 'multipart/form-data' },
    })),

  savePolicyDraft: async (id, payload) =>
    unwrap(await axiosInstance.put(`${base()}/policies/${id}/draft`, toPolicyForm(payload), {
      headers: { 'Content-Type': 'multipart/form-data' },
    })),

  discardPolicyDraft: async (id) => unwrap(await axiosInstance.delete(`${base()}/policies/${id}/draft`)),

  // { effective_date, review_date, confirmed }
  publishPolicy: async (id, payload) => unwrap(await axiosInstance.post(`${base()}/policies/${id}/publish`, payload)),

  archivePolicy: async (id) => unwrap(await axiosInstance.post(`${base()}/policies/${id}/archive`)),

  restorePolicy: async (id) => unwrap(await axiosInstance.post(`${base()}/policies/${id}/restore`)),

  getPolicyCategories: async () => unwrap(await axiosInstance.get(`${base()}/policies/categories`)),

  createPolicyCategory: async (name) => unwrap(await axiosInstance.post(`${base()}/policies/categories`, { name })),

  deletePolicyCategory: async (id) => unwrap(await axiosInstance.delete(`${base()}/policies/categories/${id}`)),

  // Patch structures the manager's own notes. It writes nothing — the result
  // comes back for the manager to apply, undo or ignore.
  runPatch: async (body) => unwrap(await axiosInstance.post(`${base()}/policies/patch`, { body })),
};

/**
 * A policy draft as multipart.
 *
 * `format` is only ever 'text' or 'upload' — WHICH kind of upload (PDF or
 * Word) is decided by the API from the file itself, so the client never has to
 * sniff a type the browser may have got wrong.
 *
 * The file field is appended only when a new one was actually chosen: sending
 * an empty one would look to the API like "the upload failed" rather than
 * "keep the file already attached".
 */
// Named `file`, not `document`: a parameter called document would shadow the
// browser global inside this function.
const toPolicyForm = ({ title, category, owner_name: owner, review_date: reviewDate, format, body, file }) => {
  const form = new FormData();
  form.append('title', title ?? '');
  form.append('category', category ?? '');
  form.append('owner_name', owner ?? '');
  form.append('review_date', reviewDate ?? '');
  form.append('format', format === 'text' ? 'text' : 'upload');
  form.append('body', body ?? '');
  if (file) form.append('document', file);
  return form;
};

export default qualityService;
